import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import type { GeoJSONSource, Map as MLMap, MapMouseEvent, Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import {
  CLOSURE_CASING,
  CLOSURE_COLORS,
  CLOSURE_CONDITION_LABELS,
  CLOSURE_CONE_COLORS,
  CLOSURE_REASON_LABELS,
  closureAnchor,
  LIGHTNING_CAVEAT_SHORT,
  LIGHTNING_RETENTION_MIN,
  KIND_COLORS,
  alertKind,
  compareClosures,
  stormSwath,
  type AreaGeometry,
  type Asset,
  type LightningFlash,
  type RoadClosure,
  type TenantAlert,
} from '@ww/shared';
import { absoluteUrl, api, type RadarFrame } from '../api';
import { I, Icon, typeIcon } from '../lib/icons';
import { computeAssetStatuses, ST, type AssetStatus } from '../lib/status';
import {
  getRadarPrefs,
  setRadarPrefs,
  RADAR_MAX_ZOOM,
  RADAR_PAINT,
  RADAR_PAINT_CATEGORICAL,
  SHARPNESS_LABELS,
  type RadarSharpness,
} from '../lib/radarPrefs';
import { fmtTime } from '../lib/time';

export interface MapTenant {
  center?: [number, number] | null;
  boundary?: AreaGeometry | null;
  timezone?: string | null;
  /** Extra alert locations, outlined more faintly than the coverage area. */
  watchAreas?: { boundary: AreaGeometry }[] | null;
}

export interface MapDraft {
  lat: number;
  lon: number;
  type: string;
}

export interface MapToolsState {
  active: boolean;
  /** Finished GeoJSON from `lib/mapTools`. */
  features: FeatureCollection;
  onMapPoint?: (lon: number, lat: number) => void;
  /** Like onMapPoint, but also reports which warning polygon was under the cursor. */
  onMapPick?: (lon: number, lat: number, hit: { alertId: string | null }) => void;
  /** Freehand: called with each completed stroke. Suspends panning while drawing. */
  onStroke?: (points: [number, number][]) => void;
  freehand?: boolean;
  /** Arrival-cone aiming: drag the handle around the origin to set a track by hand. */
  bearingOrigin?: [number, number] | null;
  bearingHandle?: [number, number] | null;
  onBearingDrag?: (bearingDeg: number) => void;
  hint?: string;
}

export interface WeatherMapProps {
  tenant: MapTenant | null | undefined;
  alerts: TenantAlert[];
  assets: Asset[];
  /** What the vertices being drawn represent. A closure is a line, an area is a polygon. */
  drawShape?: 'polygon' | 'line';
  /**
   * Satellite total lightning. Whatever draws these MUST also show the caveat — see
   * LIGHTNING_CAVEAT in @ww/shared. Passing them without it presents a ~10 km satellite
   * observation as a ground strike point.
   */
  lightning?: LightningFlash[];
  /** Operator-entered road closures. Empty or omitted hides the layer entirely. */
  closures?: RoadClosure[];
  /** Tapping a closure reports its id; the caller decides what to open. */
  onClosureClick?: (id: string) => void;
  /** Assets page: markers draggable, map clicks reported. */
  editable?: boolean;
  /** Placing mode: crosshair cursor + hint; clicks go to onMapClick instead of opening alerts. */
  placing?: boolean;
  placingHint?: string;
  /**
   * Measure / ETA / zone / markup overlay. One object rather than a prop per tool:
   * the existing `drawing` trio below means "trace the tenant coverage polygon" and
   * AreaPicker depends on that meaning, so it is left alone.
   *
   * WeatherMap stays a dumb renderer here — it owns no tool state and does no
   * geometry; the caller hands it finished features and receives raw clicks.
   */
  tools?: MapToolsState;
  /** Draw mode: each map click appends a vertex via onDrawVertex instead of opening alerts. */
  drawing?: boolean;
  /** Vertices of the polygon being drawn, in click order. The ring is closed for display. */
  drawVertices?: [number, number][];
  onDrawVertex?: (lat: number, lon: number) => void;
  /** Candidate boundary (a county/city being previewed), drawn in the accent colour. */
  previewArea?: AreaGeometry | null;
  /** Fit the view to this geometry whenever it changes. */
  fitToGeometry?: AreaGeometry | null;
  draft?: MapDraft | null;
  selectedAssetId?: string | null;
  onMapClick?: (lat: number, lon: number) => void;
  onAssetMove?: (asset: Asset, lat: number, lon: number) => void;
  onAssetClick?: (asset: Asset) => void;
  highlightAlertId?: string | null;
  /** nwsId — fit the view to this alert's geometry. */
  fitToAlert?: string | null;
  showRadar?: boolean;
  autoPlay?: boolean;
  /** Draw projected storm paths for active warnings. Off on the compact maps. */
  showSwaths?: boolean;
  /**
   * Suppress the "tap a warning polygon, open its detail page" navigation.
   *
   * For the public map, which has no session: that route is behind RequireAuth, so a
   * tap would bounce a member of the public to the sign-in screen and lose the map.
   */
  noAlertNav?: boolean;
  /** Small map (alert detail): no legend, trimmed radar controls. */
  compact?: boolean;
  showLegend?: boolean;
}

const DEFAULT_CENTER: [number, number] = [-92.29, 34.77]; // Little Rock, AR
const DEFAULT_ZOOM = 9;
// OpenFreeMap: free OpenStreetMap vector tiles, no API key or usage limits (https://openfreemap.org).
// 'liberty' is the full-colour OSM style (green parks, blue water, tan landuse); 'dark' is the muted alternative.
const BASEMAP_STYLE = 'https://tiles.openfreemap.org/styles/liberty';
const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

/**
 * Lightning flashes, carrying age in minutes so the paint can fade them.
 *
 * Age is baked in at build time rather than computed in a paint expression, because
 * MapLibre has no clock: an expression cannot know how old a dot has become since it
 * was drawn. The caller rebuilds this on its own tick.
 */
function buildLightningFeatures(flashes: LightningFlash[], nowMs: number): FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: flashes.map((f) => {
      const ageMin = Math.max(0, (nowMs - Date.parse(f.at)) / 60_000);
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [f.lon, f.lat] },
        properties: { age: ageMin },
      };
    }) as Feature[],
  };
}

/** Layers a tap on a closure can land on. Order matters only for the cursor. */
const CLOSURE_HIT_LAYERS = ['closures-line', 'closures-line-dash'];

/**
 * Worst condition LAST, so it is drawn on top and is the first hit returned by
 * queryRenderedFeatures — the same trick buildAlertFeatures uses for overlapping
 * warnings. Tapping where a closed road crosses a hazardous one must select the closure.
 */
function buildClosureFeatures(closures: RoadClosure[]): FeatureCollection {
  const sorted = [...closures].sort(compareClosures).reverse();
  return {
    type: 'FeatureCollection',
    features: sorted.map((c) => ({
      type: 'Feature',
      id: c._id,
      geometry: c.geometry as Geometry,
      properties: {
        id: c._id,
        road: c.roadName,
        cond: c.condition,
        color: CLOSURE_COLORS[c.condition],
      },
    })) as Feature[],
  };
}
/*
 * Draw order and swath ranking. Ice sits just below tornado: in Arkansas it is the event
 * that ends with a county off the grid for days. Snow ranks below flood — disruptive,
 * rarely immediately dangerous.
 */
const KIND_ORDER: Record<string, number> = { watch: 0, sps: 1, snow: 2, flood: 3, svr: 4, ice: 5, tor: 6 };
/*
 * Playback timing. 15 frames at 2-minute NOAA cadence is about half an hour of history,
 * so the loop has to move briskly or it takes ten seconds to watch once.
 */
const FRAME_MS = 420;
/** The newest frame holds longer: the loop exists to show what is happening NOW, and
 *  snapping straight back to the oldest frame is what hides it. */
const NEWEST_HOLD_MS = 1500;
const DRAW_COLOR = '#2f7bff';
const TOOL_COLOR = '#ffb454';
const ZONE_COLOR = '#4fd6ef';
const ZONE_ALERT_COLOR = '#ffa857';
const MARKUP_COLOR = '#ff7a7f';
/*
 * Neutral, not purple. Ice warnings now own the violet family, and no annotation may
 * wear a hazard colour — the same mistake as the storm swath shipping in
 * severe-thunderstorm orange.
 */
const MANUAL_COLOR = '#cbd5e8';
/*
 * Deliberately NOT a KIND_COLORS value. The first pass used #ff9f4a, which sits between
 * the severe-thunderstorm pair (#ffa857 / #ff8c1a) — a projected path that reads as a
 * warning area is the one mistake this feature must not make. One neutral colour for
 * every swath makes "this is not a hazard area" a rule rather than a shade judgement.
 */
const SWATH_COLOR = '#9fb6d8';
/** An outbreak day would otherwise paint the county with overlapping projections. */
const MAX_SWATHS = 6;
/**
 * The liberty basemap paints every feature in OpenMapTiles' `park` layer — which includes
 * national parks, protected areas and large tribal jurisdictions — in a saturated green
 * with a green outline. At regional zoom that is indistinguishable from a Flood Advisory
 * fill, so it is toned down to a neutral. Green on this map means weather, nothing else.
 */
const BASEMAP_MUTE: Array<[layer: string, prop: string, value: unknown]> = [
  ['park', 'fill-color', '#e7eade'],
  ['park', 'fill-opacity', 0.55],
  ['park', 'fill-outline-color', 'rgba(188, 196, 176, 0.9)'],
  ['park_outline', 'line-color', 'rgba(205, 212, 194, 0.9)'],
];

function validLngLat(c: unknown): c is [number, number] {
  return (
    Array.isArray(c) &&
    c.length >= 2 &&
    typeof c[0] === 'number' &&
    typeof c[1] === 'number' &&
    Number.isFinite(c[0]) &&
    Number.isFinite(c[1]) &&
    Math.abs(c[1]) <= 90 &&
    Math.abs(c[0]) <= 180
  );
}

function geomBounds(g: AreaGeometry | null | undefined): [[number, number], [number, number]] | null {
  if (!g || !Array.isArray(g.coordinates)) return null;
  const polys: number[][][][] = g.type === 'Polygon' ? [g.coordinates] : g.coordinates;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const poly of polys)
    for (const ring of poly ?? [])
      for (const pt of ring ?? []) {
        const x = pt?.[0];
        const y = pt?.[1];
        if (typeof x !== 'number' || typeof y !== 'number') continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
  if (!Number.isFinite(minX)) return null;
  return [
    [minX, minY],
    [maxX, maxY],
  ];
}

function buildAlertFeatures(alerts: TenantAlert[], highlight: string | null | undefined): FeatureCollection {
  const features: Feature[] = [];
  const sorted = alerts
    .filter((ta) => ta?.alert?.geometry)
    .slice()
    .sort((a, b) => (KIND_ORDER[alertKind(a.alert.event)] ?? 0) - (KIND_ORDER[alertKind(b.alert.event)] ?? 0));
  for (const ta of sorted) {
    const a = ta.alert;
    const kind = alertKind(a.event ?? '');
    const colors = KIND_COLORS[kind];
    const hl = !!highlight && highlight === a.nwsId;
    const isWatch = kind === 'watch';
    features.push({
      type: 'Feature',
      geometry: a.geometry as Geometry,
      properties: {
        id: a.nwsId,
        kind,
        fill: colors.fill,
        line: colors.fill,
        fo: hl ? 0.46 : isWatch ? 0.14 : 0.3,
        lw: hl ? 4 : ta.evaluation?.category === 'warning' || a.category === 'warning' ? 3 : 2,
        style: kind === 'tor' ? 'tor' : isWatch ? 'dash' : 'solid',
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** In-progress polygon: a vertex dot per click, the connecting line, and a fill once it's a ring. */
/**
 * `shape: 'line'` is for a road closure: the vertices stay an OPEN LineString instead of
 * being closed into a ring. Closing a two-point "stretch of road" into a polygon draws a
 * degenerate shape with no area, which is invisible — the operator would think their
 * taps did nothing.
 */
function buildDrawFeatures(verts: [number, number][], shape: 'polygon' | 'line' = 'polygon'): FeatureCollection {
  const features: Feature[] = verts.map((c) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: c },
    properties: {},
  }));
  if (shape === 'line') {
    if (verts.length >= 2) {
      features.unshift({ type: 'Feature', geometry: { type: 'LineString', coordinates: verts }, properties: {} });
    }
    return { type: 'FeatureCollection', features };
  }
  if (verts.length >= 3) {
    features.unshift({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [[...verts, verts[0]!]] },
      properties: {},
    });
  } else if (verts.length === 2) {
    features.unshift({ type: 'Feature', geometry: { type: 'LineString', coordinates: verts }, properties: {} });
  }
  return { type: 'FeatureCollection', features };
}

/** A cone badge for a closure: the same shape as an asset marker, so the map reads consistently. */
function buildClosureMarkerEl(c: RoadClosure): HTMLDivElement {
  const color = CLOSURE_CONE_COLORS[c.condition];
  const el = document.createElement('div');
  el.className = 'ww-marker ww-cone';
  el.setAttribute(
    'aria-label',
    `${c.roadName}: ${CLOSURE_CONDITION_LABELS[c.condition]}, ${CLOSURE_REASON_LABELS[c.reason]}`,
  );
  const dot = document.createElement('span');
  dot.className = 'ww-marker-dot';
  dot.style.borderColor = color;
  dot.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="${I.cone}" fill="none" stroke="${color}" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  el.appendChild(dot);
  const label = document.createElement('span');
  label.className = 'ww-marker-label';
  label.textContent = `${c.roadName} · ${CLOSURE_REASON_LABELS[c.reason]} · ${CLOSURE_CONDITION_LABELS[c.condition]}`;
  el.appendChild(label);
  return el;
}

function markerSvg(type: string, color: string): string {
  return `<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="${typeIcon(type)}" fill="none" stroke="${color}" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function buildMarkerEl(asset: Asset, st: AssetStatus, selected: boolean): HTMLDivElement {
  const el = document.createElement('div');
  el.className = selected ? 'ww-marker show-label' : 'ww-marker';
  const status = st.etaLabel ? `${st.label}, arriving in ${st.etaLabel.replace('~', '')}` : st.label;
  el.setAttribute('aria-label', `${asset.name}: ${status}`);
  if (st.key === 'inside') {
    const ring = document.createElement('span');
    ring.className = 'ww-marker-ring pulse';
    ring.style.borderColor = st.color;
    el.appendChild(ring);
  }
  const dot = document.createElement('span');
  dot.className = 'ww-marker-dot';
  dot.style.borderColor = selected ? '#f2f6fc' : st.color;
  dot.style.background = asset.enabled ? '#0b1424' : '#1a2233';
  if (selected) dot.style.transform = 'scale(1.2)';
  dot.innerHTML = markerSvg(asset.type, st.color);
  el.appendChild(dot);
  const label = document.createElement('span');
  label.className = 'ww-marker-label';
  label.textContent = st.etaLabel
    ? `${asset.name} · ${st.label} · ${st.etaLabel}`
    : `${asset.name} · ${st.label}`;
  el.appendChild(label);
  return el;
}

/**
 * True on a viewport too narrow for the map's overlay furniture to coexist.
 *
 * A media query cannot do this job alone: the legend and the radar settings need to
 * become things the operator opens, and "open" is state. Matches the 760px breakpoint
 * the stylesheet uses for the same decision.
 */
function useNarrowMap(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

export default function WeatherMap(props: WeatherMapProps) {
  const {
    tenant,
    alerts,
    assets,
    closures,
    lightning,
    editable = false,
    placing = false,
    placingHint,
    tools,
    drawing = false,
    drawShape = 'polygon',
    drawVertices,
    previewArea,
    fitToGeometry,
    draft,
    selectedAssetId,
    highlightAlertId,
    fitToAlert,
    showRadar = true,
    autoPlay = false,
    showSwaths,
    compact = false,
    showLegend = !compact,
  } = props;
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MLMap | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  // latest callbacks without re-binding map handlers
  const cb = useRef({ props, navigate });
  cb.current = { props, navigate };

  const safeAlerts = useMemo(() => (Array.isArray(alerts) ? alerts.filter((a) => a && a.alert) : []), [alerts]);
  const safeAssets = useMemo(
    () => (Array.isArray(assets) ? assets.filter((a) => a && validLngLat(a.location?.coordinates)) : []),
    [assets],
  );
  const statuses = useMemo(() => computeAssetStatuses(safeAssets, safeAlerts), [safeAssets, safeAlerts]);
  const tenantCenter = validLngLat(tenant?.center) ? (tenant?.center as [number, number]) : null;
  const tz = tenant?.timezone ?? undefined;

  /* ---------- init ---------- */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let map: MLMap;
    try {
      map = new maplibregl.Map({
        container: el,
        style: BASEMAP_STYLE,
        center: tenantCenter ?? DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        attributionControl: false,
        dragRotate: false,
        pitchWithRotate: false,
        transformRequest: (url) =>
          url.startsWith(`${window.location.origin}/api/`) ? { url, credentials: 'include' } : { url },
      });
    } catch (e) {
      setMapError(e instanceof Error ? e.message : 'Map failed to initialise (WebGL unavailable?)');
      return;
    }
    mapRef.current = map;
    map.touchZoomRotate.disableRotation();
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'top-left');

    map.on('load', () => {
      // Keep weather overlays under the basemap labels so place names stay readable.
      const labelLayerId = map.getStyle().layers.find((l) => l.type === 'symbol')?.id;

      for (const [layer, prop, value] of BASEMAP_MUTE) {
        if (map.getLayer(layer)) map.setPaintProperty(layer, prop, value as never);
      }
      map.addSource('boundary', { type: 'geojson', data: EMPTY_FC });
      map.addSource('watch-areas', { type: 'geojson', data: EMPTY_FC });
      map.addSource('alerts', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'alerts-fill',
        type: 'fill',
        source: 'alerts',
        paint: { 'fill-color': ['get', 'fill'], 'fill-opacity': ['get', 'fo'] },
      }, labelLayerId);
      map.addLayer({
        id: 'alerts-line',
        type: 'line',
        source: 'alerts',
        filter: ['==', ['get', 'style'], 'solid'],
        layout: { 'line-join': 'round' },
        paint: { 'line-color': ['get', 'line'], 'line-width': ['get', 'lw'], 'line-opacity': 0.9 },
      }, labelLayerId);
      map.addLayer({
        id: 'alerts-line-dash',
        type: 'line',
        source: 'alerts',
        filter: ['==', ['get', 'style'], 'dash'],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'line'],
          'line-width': ['get', 'lw'],
          'line-opacity': 0.9,
          'line-dasharray': [3, 2],
        },
      }, labelLayerId);
      map.addLayer({
        id: 'alerts-line-tor',
        type: 'line',
        source: 'alerts',
        filter: ['==', ['get', 'style'], 'tor'],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': ['get', 'line'],
          'line-width': ['max', ['get', 'lw'], 3],
          'line-opacity': 1,
        },
      }, labelLayerId);
      map.addLayer({
        id: 'boundary-line',
        type: 'line',
        source: 'boundary',
        layout: { 'line-join': 'round' },
        paint: { 'line-color': '#2b4a8c', 'line-width': 2.5, 'line-opacity': 0.95, 'line-dasharray': [4, 3] },
      }, labelLayerId);
      map.addLayer({
        id: 'watch-areas-line',
        type: 'line',
        source: 'watch-areas',
        layout: { 'line-join': 'round' },
        paint: { 'line-color': '#2b4a8c', 'line-width': 1.5, 'line-opacity': 0.7, 'line-dasharray': [2, 2] },
      }, labelLayerId);

      /*
       * Road closures, entered by the county.
       *
       * Anchored on `labelLayerId`, NOT on 'alerts-fill'. Radar frames are added
       * dynamically with `beforeId: swath-fill ?? alerts-fill`, so a closure anchored
       * there would be buried by the next frame. Here it sits above every weather
       * polygon — a shut road must never hide under a warning — and below place names
       * and below the operator's own annotations.
       *
       * Told apart by FORM rather than hue: the heavy light casing is unique on this
       * map. The hazard palette belongs to NWS polygons and the tool colours are taken,
       * so there was no free hue left that would not read as something it is not.
       */
      map.addSource('closures', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'closures-casing',
        type: 'line',
        source: 'closures',
        filter: ['==', ['geometry-type'], 'LineString'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        // Wider than the line on top, so it reads as an outline rather than a second line.
        paint: { 'line-color': CLOSURE_CASING, 'line-width': 9, 'line-opacity': 0.95 },
      }, labelLayerId);
      /*
       * TWO line layers, split by a filter, because `line-dasharray` is a `cross-faded`
       * property whose expressions may only reference zoom — not the feature. A
       * data-driven `['case', ['get','cond'], ...]` on it is rejected outright with
       * "data expressions not supported", and MapLibre answers by firing an error and
       * SKIPPING THE LAYER rather than throwing. That is how this shipped drawing only
       * its casing: a white line on a white basemap, then a black one, for five
       * releases. `alerts-line` / `alerts-line-dash` / `alerts-line-tor` above are split
       * the same way for the same reason.
       */
      map.addLayer({
        id: 'closures-line',
        type: 'line',
        source: 'closures',
        filter: ['all', ['==', ['geometry-type'], 'LineString'], ['!=', ['get', 'cond'], 'hazardous']],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': ['get', 'color'], 'line-width': 5 },
      }, labelLayerId);
      map.addLayer({
        id: 'closures-line-dash',
        type: 'line',
        source: 'closures',
        filter: ['all', ['==', ['geometry-type'], 'LineString'], ['==', ['get', 'cond'], 'hazardous']],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        // Static, so it is a legal value for this property. Dashed reads as "still
        // passable" against the solid line of a road that is shut.
        paint: { 'line-color': ['get', 'color'], 'line-width': 5, 'line-dasharray': [2, 1.5] },
      }, labelLayerId);
      /*
       * No circle layer for point closures: a cone MARKER is dropped on every closure
       * instead (see the markers effect below). A dark dot on a pale basemap said
       * nothing about what it marked — the cone says "road closed" from its shape.
       */
      map.addLayer({
        id: 'closures-label',
        type: 'symbol',
        source: 'closures',
        layout: {
          'text-field': ['get', 'road'],
          // Must be this exact string — see the note on tools-label. A font the glyph
          // set does not carry renders nothing, silently, with no console error.
          'text-font': ['Noto Sans Regular'],
          'text-size': 11,
          'text-offset': [0, 1.2],
          'text-anchor': 'top',
          'text-optional': true,
          'text-allow-overlap': false,
        },
        paint: { 'text-color': '#e7eef8', 'text-halo-color': '#0a1220', 'text-halo-width': 1.4 },
      }, labelLayerId);

      /*
       * Lightning.
       *
       * Drawn as small crosses rather than filled dots, and above everything else,
       * because a flash is an OBSERVATION of a moment rather than an area: a filled
       * circle at this size reads as a zone, which is exactly the wrong reading for
       * something GLM only locates to about ten kilometres.
       *
       * Opacity falls with age so the eye picks up which way the activity is tracking.
       * Nothing here is clickable — there is nothing more to say about one flash than
       * the map already shows, and a tap target would steal taps from the alert and
       * closure branches underneath.
       */
      map.addSource('lightning', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'lightning-flash',
        type: 'circle',
        source: 'lightning',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'age'], 0, 4.5, LIGHTNING_RETENTION_MIN, 2.5],
          'circle-color': '#ffe27a',
          'circle-opacity': [
            'interpolate',
            ['linear'],
            ['get', 'age'],
            0,
            0.95,
            LIGHTNING_RETENTION_MIN,
            0.12,
          ],
          'circle-stroke-color': '#3a2c00',
          'circle-stroke-width': 1,
          'circle-stroke-opacity': [
            'interpolate',
            ['linear'],
            ['get', 'age'],
            0,
            0.7,
            LIGHTNING_RETENTION_MIN,
            0.1,
          ],
        },
      }, labelLayerId);

      /*
       * Projected storm paths. Above the alert polygons rather than below: a radar frame
       * inserted later with `beforeId: 'alerts-fill'` would otherwise land on top of this
       * and bury it. Kept faint with a dashed edge so it reads as a projection and never
       * as the hard-edged warning polygon underneath it.
       */
      map.addSource('swath', { type: 'geojson', data: EMPTY_FC });
      map.addLayer(
        {
          id: 'swath-fill',
          type: 'fill',
          source: 'swath',
          paint: { 'fill-color': SWATH_COLOR, 'fill-opacity': ['get', 'fo'] },
        },
        'alerts-fill',
      );
      map.addLayer(
        {
          id: 'swath-line',
          type: 'line',
          source: 'swath',
          layout: { 'line-join': 'round' },
          // Always dashed and thin. NWS polygons are solid: dashed means "not official".
          paint: { 'line-color': SWATH_COLOR, 'line-width': 1.6, 'line-dasharray': [1.5, 2], 'line-opacity': 0.75 },
        },
        'alerts-fill',
      );

      // Candidate area (county/city being previewed) and the in-progress custom polygon.
      // Both sit above the labels so an admin can trace coastlines and city limits.
      map.addSource('preview', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'preview-fill',
        type: 'fill',
        source: 'preview',
        paint: { 'fill-color': DRAW_COLOR, 'fill-opacity': 0.14 },
      });
      map.addLayer({
        id: 'preview-line',
        type: 'line',
        source: 'preview',
        layout: { 'line-join': 'round' },
        paint: { 'line-color': DRAW_COLOR, 'line-width': 3, 'line-opacity': 1 },
      });

      map.addSource('draw', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'draw-fill',
        type: 'fill',
        source: 'draw',
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: { 'fill-color': DRAW_COLOR, 'fill-opacity': 0.18 },
      });
      map.addLayer({
        id: 'draw-line',
        type: 'line',
        source: 'draw',
        filter: ['!=', ['geometry-type'], 'Point'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': DRAW_COLOR, 'line-width': 2.5, 'line-dasharray': [2, 1.5] },
      });
      map.addLayer({
        id: 'draw-verts',
        type: 'circle',
        source: 'draw',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 5,
          'circle-color': '#ffffff',
          'circle-stroke-color': DRAW_COLOR,
          'circle-stroke-width': 2.5,
        },
      });
      // Tool overlay: measure lines, ETA pins, drawn zones, freehand markup.
      // No beforeId, like `preview` and `draw` — a user's own annotation must never
      // end up buried under a radar frame.
      map.addSource('tools', { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: 'tools-cone',
        type: 'fill',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'cone'],
        paint: { 'fill-color': TOOL_COLOR, 'fill-opacity': 0.1 },
      });
      map.addLayer({
        id: 'tools-fill',
        type: 'fill',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'zone'],
        paint: {
          'fill-color': ['case', ['==', ['get', 'threat'], 1], ZONE_ALERT_COLOR, ZONE_COLOR],
          'fill-opacity': 0.14,
        },
      });
      map.addLayer({
        id: 'tools-zone-line',
        type: 'line',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'zone'],
        layout: { 'line-join': 'round' },
        paint: {
          'line-color': ['case', ['==', ['get', 'threat'], 1], ZONE_ALERT_COLOR, ZONE_COLOR],
          'line-width': 2.5,
        },
      });
      map.addLayer({
        id: 'tools-line',
        type: 'line',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'measure'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': TOOL_COLOR, 'line-width': 3, 'line-dasharray': [2, 1.2] },
      });
      map.addLayer({
        id: 'tools-markup',
        type: 'line',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'markup'],
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: { 'line-color': MARKUP_COLOR, 'line-width': 3.5, 'line-opacity': 0.95 },
      });
      map.addLayer({
        id: 'tools-path-pick',
        type: 'fill',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'path-pick'],
        // Brighter than the automatic swaths, but still the same neutral hue and still
        // dashed: a tapped projection is not more official for having been asked for.
        paint: { 'fill-color': SWATH_COLOR, 'fill-opacity': 0.22 },
      });
      map.addLayer({
        id: 'tools-path-pick-line',
        type: 'line',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'path-pick'],
        layout: { 'line-join': 'round' },
        paint: { 'line-color': SWATH_COLOR, 'line-width': 2.4, 'line-dasharray': [3, 2] },
      });
      map.addLayer({
        id: 'tools-cone-manual',
        type: 'fill',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'cone-manual'],
        paint: { 'fill-color': MANUAL_COLOR, 'fill-opacity': 0.16 },
      });
      map.addLayer({
        id: 'tools-aim',
        type: 'line',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'aim'],
        layout: { 'line-cap': 'round' },
        paint: { 'line-color': MANUAL_COLOR, 'line-width': 2, 'line-dasharray': [1.5, 1.5], 'line-opacity': 0.9 },
      });
      map.addLayer({
        id: 'tools-handle',
        type: 'circle',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'handle'],
        paint: {
          'circle-radius': 8,
          'circle-color': MANUAL_COLOR,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2.5,
        },
      });
      map.addLayer({
        id: 'tools-verts',
        type: 'circle',
        source: 'tools',
        filter: ['in', ['get', 'k'], ['literal', ['vert', 'pin']]],
        paint: {
          'circle-radius': ['case', ['==', ['get', 'k'], 'pin'], 7, 5],
          'circle-color': '#ffffff',
          'circle-stroke-color': TOOL_COLOR,
          'circle-stroke-width': 3,
        },
      });
      map.addLayer({
        id: 'tools-label',
        type: 'symbol',
        source: 'tools',
        filter: ['==', ['get', 'k'], 'label'],
        layout: {
          'text-field': ['get', 'label'],
          // Must exist in the OpenFreeMap liberty glyph set, or labels render as
          // nothing at all, silently and with no console error.
          'text-font': ['Noto Sans Regular'],
          'text-size': 13,
          'text-offset': [0, -1.5],
          'text-allow-overlap': true,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#0b1424',
          'text-halo-width': 2,
        },
      });

      setLoaded(true);
    });

    map.on('error', (ev) => {
      const msg = ev.error?.message ?? String(ev);
      /*
       * Tile failures are expected whenever an upstream is down and are pure noise.
       * Everything else is a bug in OUR style, and swallowing those in production is
       * what let a rejected layer go unnoticed through five releases — the map simply
       * drew one fewer thing and said nothing. Those get logged wherever they happen.
       */
      const isTileNoise = /tile|fetch|network|abort|http/i.test(msg);
      if (isTileNoise) {
        if (import.meta.env.DEV) console.debug('[map]', msg);
      } else {
        console.warn('[map] style error —', msg);
      }
    });

    map.on('mousemove', (e: MapMouseEvent) => {
      if (!map.getLayer('alerts-fill')) return;
      const { placing: isPlacing, drawing: isDrawing, tools: t } = cb.current.props;
      const hitLayers = ['alerts-fill', ...CLOSURE_HIT_LAYERS.filter((l) => map.getLayer(l))];
      const hit = map.queryRenderedFeatures(e.point, { layers: hitLayers }).length > 0;
      if (t?.bearingHandle) {
        const h = map.project(t.bearingHandle);
        if (Math.hypot(e.point.x - h.x, e.point.y - h.y) <= 20) {
          map.getCanvas().style.cursor = 'grab';
          return;
        }
      }
      map.getCanvas().style.cursor = isPlacing || isDrawing || t?.active ? 'crosshair' : hit ? 'pointer' : '';
    });

    map.on('click', (e: MapMouseEvent) => {
      const p = cb.current.props;
      if (p.drawing && p.onDrawVertex) {
        p.onDrawVertex(e.lngLat.lat, e.lngLat.lng);
        return;
      }
      if (p.placing && p.onMapClick) {
        p.onMapClick(e.lngLat.lat, e.lngLat.lng);
        return;
      }
      /*
       * Tool taps are handled before the alert-navigation branch below, which is what
       * stops the storm-path tool from opening the alert detail page instead. The
       * ordering is load-bearing; do not move these.
       */
      if (p.tools?.active && p.tools.onMapPick) {
        const hits = map.getLayer('alerts-fill')
          ? map.queryRenderedFeatures(e.point, { layers: ['alerts-fill'] })
          : [];
        // buildAlertFeatures sorts by KIND_ORDER, so the most severe overlapping warning
        // is drawn last and is therefore the first hit here.
        const alertId = hits[0]?.properties?.id;
        p.tools.onMapPick(e.lngLat.lng, e.lngLat.lat, {
          alertId: typeof alertId === 'string' ? alertId : null,
        });
        return;
      }
      if (p.tools?.active && p.tools.onMapPoint) {
        p.tools.onMapPoint(e.lngLat.lng, e.lngLat.lat);
        return;
      }
      /*
       * Closures are tested BEFORE the alert branch below, and the position matters as
       * much as the ones above. A closure is drawn on top of the warning polygon and is
       * the smaller, more specific target; if the alert branch ran first, tapping a
       * barricade would open a tornado warning instead. Same rule as the tool branches:
       * the ordering is load-bearing, do not move this.
       */
      const closureLayers = CLOSURE_HIT_LAYERS.filter((l) => map.getLayer(l));
      if (closureLayers.length && p.onClosureClick) {
        const chits = map.queryRenderedFeatures(e.point, { layers: closureLayers });
        const cid = chits[0]?.properties?.id;
        if (typeof cid === 'string' && cid) {
          p.onClosureClick(cid);
          return;
        }
      }
      const feats =
        !p.noAlertNav && map.getLayer('alerts-fill')
          ? map.queryRenderedFeatures(e.point, { layers: ['alerts-fill'] })
          : [];
      const id = feats[0]?.properties?.id;
      if (typeof id === 'string' && id) {
        cb.current.navigate(`/alerts/${encodeURIComponent(id)}`);
        return;
      }
      p.onMapClick?.(e.lngLat.lat, e.lngLat.lng);
    });

    const ro = new ResizeObserver(() => map.resize());
    ro.observe(el);

    return () => {
      ro.disconnect();
      setLoaded(false);
      mapRef.current = null;
      map.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------- tenant centre + boundary ---------- */
  /**
   * Which tenant location the view is currently showing. Keyed by value rather than object
   * identity: the tenant object is rebuilt from JSON on every poll, so identity would retrigger
   * constantly, while a one-shot boolean (the previous approach) meant switching tenants left
   * the map sitting on the old county.
   */
  const centeredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;

    const b = tenant?.boundary;
    const src = map.getSource('boundary') as GeoJSONSource | undefined;
    src?.setData(
      b && Array.isArray(b.coordinates)
        ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: b as Geometry, properties: {} }] }
        : EMPTY_FC,
    );

    // The alert-detail view owns its viewport; don't fight it.
    if (fitToAlert) return;

    const bounds = geomBounds(b);
    const key = bounds ? `b:${bounds.flat().join(',')}` : tenantCenter ? `c:${tenantCenter.join(',')}` : null;
    if (!key || key === centeredKeyRef.current) return;
    // First load lands instantly; later tenant switches animate so the move is legible.
    const first = centeredKeyRef.current === null;
    centeredKeyRef.current = key;

    if (bounds) {
      map.fitBounds(bounds, { padding: compact ? 30 : 60, maxZoom: 11, duration: first ? 0 : 800 });
    } else if (tenantCenter) {
      map.easeTo({ center: tenantCenter, zoom: DEFAULT_ZOOM, duration: first ? 0 : 800 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, tenantCenter?.[0], tenantCenter?.[1], tenant?.boundary, fitToAlert, compact]);

  /* ---------- extra alert locations ---------- */
  // Keyed by value: the tenant object is rebuilt on every poll.
  const watchKey = JSON.stringify(tenant?.watchAreas?.map((w) => w.boundary) ?? []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const src = map.getSource('watch-areas') as GeoJSONSource | undefined;
    src?.setData({
      type: 'FeatureCollection',
      features: (tenant?.watchAreas ?? []).map((w) => ({
        type: 'Feature' as const,
        geometry: w.boundary as Geometry,
        properties: {},
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, watchKey]);

  /* ---------- alert polygons ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const src = map.getSource('alerts') as GeoJSONSource | undefined;
    src?.setData(buildAlertFeatures(safeAlerts, highlightAlertId));
  }, [loaded, safeAlerts, highlightAlertId]);

  /* ---------- candidate area preview ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const src = map.getSource('preview') as GeoJSONSource | undefined;
    src?.setData(
      previewArea && Array.isArray(previewArea.coordinates)
        ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: previewArea as Geometry, properties: {} }] }
        : EMPTY_FC,
    );
  }, [loaded, previewArea]);

  /* ---------- custom polygon being drawn ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const src = map.getSource('draw') as GeoJSONSource | undefined;
    src?.setData(buildDrawFeatures(drawVertices ?? [], drawShape));
  }, [loaded, drawVertices, drawShape]);

  // Rapid vertex clicks would otherwise trip double-click zoom.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    if (drawing || tools?.active) map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
  }, [loaded, drawing, tools?.active]);

  /* ---------- tool overlay ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const src = map.getSource('tools') as GeoJSONSource | undefined;
    src?.setData(tools?.features ?? EMPTY_FC);
  }, [loaded, tools?.features]);

  const canAim = !!(tools?.bearingHandle && tools.bearingOrigin && tools.onBearingDrag);

  /**
   * Dragging the arrival cone's handle to aim a track by hand.
   *
   * Computed in screen space rather than by unprojecting to lon/lat and doing spherical
   * trigonometry: the user is aiming at what they can see, so the angle they mean is the
   * angle on the screen. Panning is suspended for the drag, and re-enabled in the effect
   * cleanup rather than on pointerup, so an interrupted drag cannot strand the map.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !canAim) return;
    const canvas = map.getCanvas();
    let dragging = false;

    const pointOf = (ev: PointerEvent): { x: number; y: number } => {
      const r = canvas.getBoundingClientRect();
      return { x: ev.clientX - r.left, y: ev.clientY - r.top };
    };
    const aim = (ev: PointerEvent) => {
      const t = cb.current.props.tools;
      if (!t?.bearingOrigin || !t.onBearingDrag) return;
      const o = map.project(t.bearingOrigin);
      const p = pointOf(ev);
      // Screen y grows downward, so north is -y.
      const deg = (Math.atan2(p.x - o.x, o.y - p.y) * 180) / Math.PI;
      t.onBearingDrag(((deg % 360) + 360) % 360);
    };
    const down = (ev: PointerEvent) => {
      const t = cb.current.props.tools;
      if (!t?.bearingHandle) return;
      const h = map.project(t.bearingHandle);
      const p = pointOf(ev);
      /*
       * Generous hit radius: this is an 8px dot that people grab in a hurry, and a
       * fingertip lands far less precisely than a cursor. 26px is a 52px target, just
       * over the 44px touch minimum; going wider would start stealing pans meant for
       * the map, which on a phone-sized canvas is most of the screen.
       */
      const grab = ev.pointerType === 'mouse' ? 20 : 26;
      if (Math.hypot(p.x - h.x, p.y - h.y) > grab) return;
      dragging = true;
      map.dragPan.disable();
      canvas.setPointerCapture?.(ev.pointerId);
      ev.preventDefault();
      ev.stopPropagation();
      aim(ev);
    };
    const move = (ev: PointerEvent) => {
      if (!dragging) return;
      aim(ev);
      ev.preventDefault();
    };
    const up = () => {
      if (!dragging) return;
      dragging = false;
      map.dragPan.enable();
    };

    // Capture phase: MapLibre's own drag-pan handler is bound to the same canvas, and
    // whichever registered first wins the bubble phase. Grabbing the event on the way
    // down means a pan can never start under the handle.
    canvas.addEventListener('pointerdown', down, { capture: true });
    canvas.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      canvas.removeEventListener('pointerdown', down, { capture: true });
      canvas.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      map.dragPan.enable();
    };
    /*
     * Deliberately NOT keyed on the handle's position. Dragging moves the handle, which
     * would re-run this effect, tear the listeners down mid-gesture and reset `dragging`
     * — the drag would die after a single pointermove. Live values are read from the
     * props ref inside the handlers instead.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, canAim]);

  /**
   * Freehand markup. Panning has to be suspended for the duration of a stroke or
   * every scribble drags the map instead. Re-enabled in the cleanup rather than in
   * the pointerup handler, so an interrupted stroke can never strand the user with
   * a map they cannot move.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !tools?.freehand) return;
    const canvas = map.getCanvas();
    let points: [number, number][] = [];
    let drawingStroke = false;

    const toLngLat = (ev: PointerEvent): [number, number] => {
      const r = canvas.getBoundingClientRect();
      const ll = map.unproject([ev.clientX - r.left, ev.clientY - r.top]);
      return [ll.lng, ll.lat];
    };
    const down = (ev: PointerEvent) => {
      if (ev.button !== 0 && ev.pointerType === 'mouse') return;
      drawingStroke = true;
      points = [toLngLat(ev)];
      map.dragPan.disable();
      canvas.setPointerCapture?.(ev.pointerId);
      ev.preventDefault();
    };
    const move = (ev: PointerEvent) => {
      if (!drawingStroke) return;
      points.push(toLngLat(ev));
      ev.preventDefault();
    };
    const up = () => {
      if (!drawingStroke) return;
      drawingStroke = false;
      map.dragPan.enable();
      if (points.length > 1) cb.current.props.tools?.onStroke?.(points);
      points = [];
    };

    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      map.dragPan.enable();
    };
  }, [loaded, tools?.freehand]);

  /* ---------- fit to an arbitrary geometry ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !fitToGeometry) return;
    const bounds = geomBounds(fitToGeometry);
    if (bounds) map.fitBounds(bounds, { padding: compact ? 30 : 60, maxZoom: 12, duration: 600 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, fitToGeometry]);

  const hasTornado = useMemo(() => safeAlerts.some((a) => a.alert.geometry && alertKind(a.alert.event) === 'tor'), [safeAlerts]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !hasTornado) return;
    const start = performance.now();
    const t = window.setInterval(() => {
      // 1.6 s ease cycle between 1 and 0.35 (mockup `wwtor`)
      const phase = ((performance.now() - start) % 1600) / 1600;
      const v = 0.675 + 0.325 * Math.cos(phase * 2 * Math.PI);
      if (map.getLayer('alerts-line-tor')) map.setPaintProperty('alerts-line-tor', 'line-opacity', v);
    }, 100);
    return () => {
      window.clearInterval(t);
      if (map.getLayer('alerts-line-tor')) map.setPaintProperty('alerts-line-tor', 'line-opacity', 1);
    };
  }, [loaded, hasTornado]);

  /* ---------- fit to alert ---------- */
  const fittedRef = useRef<string | null>(null);
  const fitAlertBounds = (map: MLMap, id: string, animate: boolean): boolean => {
    const ta = safeAlerts.find((a) => a.alert.nwsId === id);
    const bounds = geomBounds(ta?.alert.geometry);
    if (!bounds) return false;
    map.fitBounds(bounds, { padding: compact ? 30 : 60, maxZoom: 11, duration: animate ? 600 : 0 });
    return true;
  };
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !fitToAlert || fittedRef.current === fitToAlert) return;
    if (fitAlertBounds(map, fitToAlert, false)) {
      fittedRef.current = fitToAlert;
    } else if (safeAlerts.length && tenantCenter) {
      map.jumpTo({ center: tenantCenter, zoom: 8 });
      fittedRef.current = fitToAlert;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, fitToAlert, safeAlerts]);

  /* ---------- asset markers ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const markers: Marker[] = [];
    for (const asset of safeAssets) {
      const st = statuses.get(asset._id) ?? (asset.enabled ? ST.clear : ST.disabled);
      const el = buildMarkerEl(asset, st, selectedAssetId === asset._id);
      const marker = new maplibregl.Marker({ element: el, draggable: editable })
        .setLngLat(asset.location.coordinates as [number, number])
        .addTo(map);
      let dragged = false;
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (dragged) {
          dragged = false;
          return;
        }
        el.classList.toggle('show-label');
        cb.current.props.onAssetClick?.(asset);
      });
      if (editable) {
        marker.on('dragstart', () => {
          dragged = true;
        });
        marker.on('dragend', () => {
          const ll = marker.getLngLat();
          cb.current.props.onAssetMove?.(asset, ll.lat, ll.lng);
          window.setTimeout(() => {
            dragged = false;
          }, 0);
        });
      }
      markers.push(marker);
    }
    return () => markers.forEach((m) => m.remove());
  }, [loaded, safeAssets, statuses, editable, selectedAssetId]);

  /* ---------- draft marker (new asset being placed) ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded || !draft || !validLngLat([draft.lon, draft.lat])) return;
    const el = document.createElement('div');
    el.className = 'ww-marker ww-draft show-label';
    el.innerHTML = `<span class="ww-marker-ring pulse" style="border-color:#5aa9ff"></span><span class="ww-marker-dot" style="border-color:#5aa9ff">${markerSvg(draft.type, '#5aa9ff')}</span>`;
    const label = document.createElement('span');
    label.className = 'ww-marker-label';
    label.textContent = 'New asset';
    el.appendChild(label);
    const m = new maplibregl.Marker({ element: el }).setLngLat([draft.lon, draft.lat]).addTo(map);
    return () => {
      m.remove();
    };
  }, [loaded, draft?.lat, draft?.lon, draft?.type]);

  /* ---------- radar ---------- */
  const [frames, setFrames] = useState<RadarFrame[]>([]);
  const [frameIdx, setFrameIdx] = useState(0);
  const [playing, setPlaying] = useState(autoPlay);
  // Seeded from storage so a viewer's choice survives a remount, and written back on
  // change. `tenantId` may be null on the public alert page; the prefs module no-ops.
  const tenantId = (tenant as { _id?: string } | null | undefined)?._id ?? null;
  const [prefs, setPrefs] = useState(() => getRadarPrefs(tenantId));
  const opacity = prefs.opacity;
  const sharpness = prefs.sharpness;
  const [providerMaxZoom, setProviderMaxZoom] = useState<number | null>(null);
  /*
   * The sharpness setting is a preference; the provider's limit is a fact. RainViewer's
   * free tiles carry real data only to z7 and serve one identical placeholder above it,
   * so asking for more paints nonsense. Take whichever is lower.
   */
  const radarMaxZoom = Math.min(RADAR_MAX_ZOOM[sharpness], providerMaxZoom ?? 22);
  const swathMinutes = prefs.swathMinutes;
  const [swathCount, setSwathCount] = useState(0);

  /* ---------- lightning ---------- */
  /*
   * Its own clock. Flashes fade with age, and without a tick they would freeze at
   * whatever opacity they had when the data last changed — a five-minute-old flash
   * still looking brand new. 20s is well inside the shortest visible fade step.
   */
  const [ltgTick, setLtgTick] = useState(() => Date.now());
  useEffect(() => {
    if (!lightning?.length) return;
    const t = window.setInterval(() => setLtgTick(Date.now()), 20_000);
    return () => window.clearInterval(t);
  }, [lightning?.length]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const src = map.getSource('lightning') as GeoJSONSource | undefined;
    src?.setData(buildLightningFeatures(lightning ?? [], ltgTick));
  }, [loaded, lightning, ltgTick]);

  /* ---------- road closures ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const src = map.getSource('closures') as GeoJSONSource | undefined;
    src?.setData(buildClosureFeatures(closures ?? []));
  }, [loaded, closures]);

  /*
   * One cone per closure. A stretch of road keeps its drawn line for the extent, and
   * gets the cone at its midpoint so the marker does not sit on an arbitrary end.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const markers: Marker[] = [];
    for (const c of closures ?? []) {
      const el = buildClosureMarkerEl(c);
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        el.classList.toggle('show-label');
        cb.current.props.onClosureClick?.(c._id);
      });
      markers.push(new maplibregl.Marker({ element: el }).setLngLat(closureAnchor(c.geometry)).addTo(map));
    }
    return () => markers.forEach((m) => m.remove());
  }, [loaded, closures]);
  const narrow = useNarrowMap();
  // Open by default on a wide screen, where they have always simply been visible.
  const [legendOpen, setLegendOpen] = useState(false);
  const [radarSettingsOpen, setRadarSettingsOpen] = useState(false);

  /* ---------- projected storm paths ---------- */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const src = map.getSource('swath') as GeoJSONSource | undefined;
    if (!src) return;
    if (!showSwaths) {
      src.setData(EMPTY_FC);
      return;
    }
    const nowMsLocal = Date.now();
    const candidates: Array<{ ta: TenantAlert; swath: AreaGeometry }> = [];
    for (const ta of safeAlerts) {
      // Only warnings, and only ones carrying a real track. A watch covers a county for
      // hours and has no storm to project; an alert without motion has no direction.
      const cat = ta.evaluation?.category ?? ta.alert.category;
      if (cat !== 'warning' || !ta.alert.geometry || !ta.alert.motion) continue;
      /*
       * `etaToPoint` refuses a vector older than 30 minutes, but `stormSwath` takes no
       * clock and would happily draw one. Without this the map shows a confident path
       * above an impacted list that is empty — a visible contradiction.
       */
      const ageMin = (nowMsLocal - new Date(ta.alert.motion.observedAt).getTime()) / 60_000;
      if (!(ageMin <= 30)) continue;
      const swath = stormSwath(ta.alert.geometry, ta.alert.motion, swathMinutes);
      if (swath) candidates.push({ ta, swath });
    }
    // Most severe first, so the cap drops the least important paths.
    candidates.sort(
      (a, b) =>
        (KIND_ORDER[alertKind(b.ta.alert.event)] ?? 0) - (KIND_ORDER[alertKind(a.ta.alert.event)] ?? 0),
    );
    const shown = candidates.slice(0, MAX_SWATHS);
    // Fade back as they pile up; overlapping projections otherwise read as a hazard field.
    const fo = shown.length <= 2 ? 0.16 : shown.length <= 4 ? 0.11 : 0.08;
    src.setData({
      type: 'FeatureCollection',
      features: shown.map(({ ta, swath }) => ({
        type: 'Feature',
        geometry: swath as Geometry,
        properties: { id: ta.alert.nwsId, fo },
      })),
    });
    setSwathCount(shown.length);
  }, [loaded, safeAlerts, showSwaths, swathMinutes]);

  const updatePrefs = (patch: Partial<typeof prefs>) => setPrefs(setRadarPrefs(tenantId, patch));
  const [providerOptions, setProviderOptions] = useState<Array<{ id: string; label: string }>>([]);
  const [productOptions, setProductOptions] = useState<Array<{ id: string; label: string; categorical: boolean }>>([]);
  const activeProduct = productOptions.find((p) => p.id === (prefs.product ?? 'reflectivity'));
  const categorical = activeProduct?.categorical ?? false;
  const [attribution, setAttribution] = useState<string | null>(null);
  const [radarIssue, setRadarIssue] = useState<string | null>(null);
  const framesRef = useRef<RadarFrame[]>([]);
  const playingRef = useRef(playing);
  playingRef.current = playing;

  useEffect(() => {
    setPrefs(getRadarPrefs(tenantId));
  }, [tenantId]);

  useEffect(() => {
    if (!showRadar) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.radarFrames(prefs.provider, prefs.product);
        if (cancelled) return;
        const list = (Array.isArray(r?.frames) ? r.frames : [])
          .filter((f): f is RadarFrame => !!f && typeof f.url === 'string' && typeof f.time === 'string')
          .sort((a, b) => a.time.localeCompare(b.time));
        const prev = framesRef.current;
        const same =
          prev.length === list.length && prev.every((f, i) => f.time === list[i]?.time && f.url === list[i]?.url);
        if (!same) {
          framesRef.current = list;
          setFrames(list);
          setFrameIdx((i) => (playingRef.current && list.length ? i % list.length : Math.max(0, list.length - 1)));
        }
        if (Array.isArray(r?.providers)) setProviderOptions(r.providers);
        if (Array.isArray(r?.products)) setProductOptions(r.products);
        setAttribution(typeof r?.attribution === 'string' ? r.attribution : null);
        setProviderMaxZoom(typeof r?.maxZoom === 'number' ? r.maxZoom : null);
        const st = r?.status?.status;
        if (st && st !== 'ok') setRadarIssue(r.status.detail || `Radar ${st}`);
        else if (!list.length) setRadarIssue('No radar frames available');
        else setRadarIssue(null);
      } catch {
        if (!cancelled) setRadarIssue('Radar unavailable');
      }
    };
    void load();
    const t = window.setInterval(() => void load(), 120_000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [showRadar, prefs.provider, prefs.product]);

  const radarIdsRef = useRef<string[]>([]);
  /*
   * Source ids are `radar-<time>`, and both products publish the SAME timestamps on the
   * same provider. Reconciling by id alone therefore treats a product switch as "nothing
   * changed" and leaves the old product's tiles on screen. Key the teardown on everything
   * that changes what a source fetches, not just the zoom cap.
   */
  const builtKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const wanted = frames.map((f) => `radar-${f.time}`);
    // A raster source's maxzoom is fixed at addSource time, so a sharpness change means
    // tearing everything down rather than reconciling. Bounded by the ref, so clicking
    // the same mode twice rebuilds nothing.
    const buildKey = `${radarMaxZoom}:${prefs.provider ?? ''}:${prefs.product ?? ''}`;
    const rebuildAll = builtKeyRef.current !== null && builtKeyRef.current !== buildKey;
    for (const id of rebuildAll ? radarIdsRef.current : radarIdsRef.current.filter((i) => !wanted.includes(i))) {
      if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(id)) map.removeSource(id);
    }
    if (rebuildAll) radarIdsRef.current = [];
    builtKeyRef.current = buildKey;
    frames.forEach((f, i) => {
      const id = wanted[i];
      if (!id || map.getSource(id)) return;
      try {
        map.addSource(id, {
          type: 'raster',
          tiles: [absoluteUrl(f.url)],
          tileSize: 256,
          // Past this the upstream only redraws the same 1.1 km cells bigger, so let
          // the GPU upscale instead: smoother, and it stops the pointless requests.
          maxzoom: radarMaxZoom,
          ...(attribution ? { attribution } : {}),
        });
        map.addLayer(
          {
            id,
            type: 'raster',
            source: id,
            // Hidden, not transparent. MapLibre still downloads every tile of a layer at
            // opacity 0, so eight frames meant eight times the tile requests on every
            // pan — the burst that got this deployment throttled by NOAA. `visibility:
            // none` is the only state in which a layer fetches nothing.
            layout: { visibility: 'none' },
            paint: { 'raster-opacity': 0, 'raster-fade-duration': 0 },
          },
          // Under the swath, not just under the alerts: radar frames are added
          // dynamically, and `beforeId: 'alerts-fill'` would put each new one on top of
          // the swath and wash out its outline.
          map.getLayer('swath-fill') ? 'swath-fill' : map.getLayer('alerts-fill') ? 'alerts-fill' : undefined,
        );
      } catch (e) {
        if (import.meta.env.DEV) console.debug('[radar] add failed', e);
      }
    });
    radarIdsRef.current = wanted;
  }, [loaded, frames, radarMaxZoom, attribution, prefs.provider, prefs.product]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    radarIdsRef.current.forEach((id, i) => {
      if (!map.getLayer(id)) return;
      /*
       * One frame is live at a time. While the loop is playing the next frame is shown
       * too (at zero opacity) so its tiles are already in the browser cache when its
       * turn comes and the animation does not stutter — a one-frame lookahead instead of
       * the eight-frame broadside that was being fired before.
       */
      const isCurrent = i === frameIdx;
      const isNext = playing && frames.length > 1 && i === (frameIdx + 1) % frames.length;
      const visible = showRadar && (isCurrent || isNext);
      map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
      if (!visible) return;
      map.setPaintProperty(id, 'raster-opacity', isCurrent ? opacity : 0);
      // Applied alongside visibility so look and state never disagree for a frame.
      const paint = categorical ? RADAR_PAINT_CATEGORICAL : RADAR_PAINT[sharpness];
      map.setPaintProperty(id, 'raster-resampling', paint['raster-resampling']);
      map.setPaintProperty(id, 'raster-saturation', paint['raster-saturation']);
      map.setPaintProperty(id, 'raster-contrast', paint['raster-contrast']);
    });
  }, [loaded, frames, frameIdx, opacity, showRadar, playing, sharpness, radarMaxZoom, categorical]);

  useEffect(() => {
    if (!playing || frames.length < 2) return;
    // One timeout per frame rather than a fixed interval, so the dwell can depend on
    // which frame is showing.
    const atNewest = frameIdx === frames.length - 1;
    const t = window.setTimeout(
      () => setFrameIdx((i) => (i + 1) % frames.length),
      atNewest ? NEWEST_HOLD_MS : FRAME_MS,
    );
    return () => window.clearTimeout(t);
  }, [playing, frameIdx, frames.length]);

  /* ---------- controls ---------- */
  const zoomBy = (d: number) => {
    const map = mapRef.current;
    if (!map) return;
    if (d > 0) map.zoomIn();
    else map.zoomOut();
  };
  const recenter = () => {
    const map = mapRef.current;
    if (!map) return;
    if (fitToAlert && fitAlertBounds(map, fitToAlert, true)) return;
    map.easeTo({ center: tenantCenter ?? DEFAULT_CENTER, zoom: DEFAULT_ZOOM, duration: 600 });
  };

  const frame = frames[frameIdx];
  const latest = frameIdx === frames.length - 1;
  // How far back this frame sits from the newest one. With half an hour of history a
  // bare clock time makes you do the subtraction yourself.
  const minutesBack =
    frame && frames.length
      ? Math.round((new Date(frames[frames.length - 1]!.time).getTime() - new Date(frame.time).getTime()) / 60_000)
      : 0;
  const spanMinutes =
    frames.length > 1
      ? Math.round((new Date(frames[frames.length - 1]!.time).getTime() - new Date(frames[0]!.time).getTime()) / 60_000)
      : 0;

  return (
    <div
      className={
        // `map-placing` also carries `touch-action: none`, which is what stops a
        // freehand stroke from being taken as a page scroll on a touchscreen.
        placing || drawing || tools?.freehand ? 'map-wrap map-placing' : 'map-wrap'
      }
    >
      <div ref={containerRef} className="map-canvas" />

      {mapError && (
        <div className="map-overlay" style={{ left: 12, top: 12, padding: '8px 12px', fontSize: 13, color: '#ffb3b7' }}>
          Map unavailable: {mapError}
        </div>
      )}

      {(placing || drawing || (tools?.active && tools.hint)) && (
        <div className="placing-hint">
          <Icon d={I.crosshair} size={18} />
          {placing || drawing
            ? placingHint ??
              (drawing
                ? 'Click to add corners — at least three make an area'
                : 'Click anywhere on the map to set this asset’s location')
            : tools?.hint}
        </div>
      )}

      {!!lightning?.length && (
        /*
         * Rendered HERE, inside the component that draws the flashes, rather than left
         * to each caller. A dot on a map says "lightning struck at this point"; GLM
         * cannot support that reading, and the label is the only thing standing between
         * the two. A caller must not be able to have the dots without it.
         */
        <div className="map-overlay ltg-note" role="note">
          <span className="ltg-dot" aria-hidden />
          <span>{LIGHTNING_CAVEAT_SHORT}</span>
        </div>
      )}

      {showRadar && radarIssue && (
        <div className="map-overlay map-chip" role="status" style={compact ? { top: 8, right: 8 } : undefined}>
          <span className="dot" style={{ background: '#ffb547' }} />
          <span>
            <strong style={{ color: '#ffc36b' }}>Radar degraded</strong>
            {!compact && <span className="muted"> · {radarIssue}. Warnings and asset alerting continue.</span>}
          </span>
        </div>
      )}

      {showLegend && narrow && (
        /* Eleven nowrap rows is roughly 200x180px — half of a phone's map. Behind a
           toggle it stays available without permanently covering the weather. */
        <button
          type="button"
          className="map-overlay legend-toggle"
          aria-expanded={legendOpen}
          onClick={() => setLegendOpen((v) => !v)}
        >
          {legendOpen ? 'Hide key' : 'Key'}
        </button>
      )}

      {showLegend && (!narrow || legendOpen) && (
        <div className={narrow ? 'map-overlay legend legend-narrow' : 'map-overlay legend'} aria-label="Map legend">
          <div><span className="sw" style={{ background: '#ff3b47' }} />Tornado Warning</div>
          <div><span className="sw-ring" style={{ borderColor: '#ff7a7f' }} />Asset in warning</div>
          <div><span className="sw" style={{ background: '#ff8c1a' }} />Svr T-storm Warning</div>
          <div><span className="sw-ring" style={{ borderColor: '#ffa857' }} />Within radius</div>
          <div><span className="sw" style={{ border: '2px dashed #f5c518' }} />Watch (county)</div>
          <div><span className="sw-ring" style={{ borderColor: '#78b7ff' }} />Asset clear</div>
          <div><span className="sw" style={{ background: '#00a9c7' }} />Flood Advisory</div>
          <div><span className="sw" style={{ background: '#a855f7' }} />Ice Storm Warning</div>
          <div><span className="sw" style={{ background: '#ff69b4' }} />Winter/snow</div>
          <div><span className="sw" style={{ background: '#d9b27a' }} />Special statement</div>
          <div>
            <span
              className="sw"
              style={{ background: 'transparent', border: `1.5px dashed ${SWATH_COLOR}` }}
            />
            Projected path (estimate)
          </div>
        </div>
      )}

      {swathCount > 0 && !compact && (
        /*
         * Inside the map canvas, not in a legend or a page banner. The markup tool exists
         * so people photograph this screen and send it on; a caption outside the map does
         * not travel with the picture, and neither does an unattended wallboard's footer.
         */
        <div
          className="map-overlay"
          role="note"
          style={{
            left: 12,
            top: 12,
            padding: '6px 10px',
            fontSize: 11.5,
            color: '#c9d7ee',
            maxWidth: 260,
            lineHeight: 1.4,
          }}
        >
          Dashed areas are <strong>projected storm paths</strong> — estimates from the current track,
          not NWS warning areas.
        </div>
      )}

      {showRadar && frames.length > 0 && (
        <div
          className="map-overlay radar-bar"
          style={compact ? { right: 58, bottom: 10, borderRadius: 22 } : { borderRadius: 22 }}
        >
          <button
            type="button"
            className="play-btn"
            aria-label={playing ? 'Pause radar loop' : 'Play radar loop'}
            onClick={() => setPlaying((p) => !p)}
          >
            <Icon d={playing ? I.pause : I.play} size={16} strokeWidth={2.4} />
          </button>
          <input
            type="range"
            min={0}
            max={Math.max(0, frames.length - 1)}
            step={1}
            value={frameIdx}
            aria-label={`Radar frame, ${spanMinutes} minute loop`}
            title={spanMinutes ? `${spanMinutes} minutes of radar history` : undefined}
            style={{ width: compact ? 80 : 150 }}
            onChange={(e) => {
              setPlaying(false);
              setFrameIdx(Number(e.target.value));
            }}
          />
          <span
            className="mono"
            style={{ minWidth: compact ? 74 : 96, color: latest ? '#eef3fa' : '#9fb0c8' }}
            aria-live="off"
          >
            {frame ? fmtTime(frame.time, tz) : '—'}
            {latest ? ' ·now' : minutesBack ? ` ·-${minutesBack}m` : ''}
          </span>
          {!compact && narrow && (
            /* The settings group is `nowrap` and about 400px wide; on a phone it ran
               straight off the left edge and was clipped by the map's overflow. */
            <button
              type="button"
              className="radar-gear"
              aria-expanded={radarSettingsOpen}
              aria-label="Radar display settings"
              onClick={() => setRadarSettingsOpen((v) => !v)}
            >
              <Icon d={I.gear} size={15} />
            </button>
          )}
          {!compact && (!narrow || radarSettingsOpen) && (
            // Grouped so the bar breaks between the timeline and the settings when the
            // map is narrow, instead of stranding a lone control on the second row.
            <div className={narrow ? 'radar-settings radar-settings-open' : 'radar-settings'}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>Opacity</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={opacity}
                  style={{ width: 70 }}
                  onChange={(e) => updatePrefs({ opacity: Number(e.target.value) })}
                />
              </label>
              <div className="radar-seg" role="group" aria-label="Radar detail">
                {SHARPNESS_LABELS.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={sharpness === key}
                    title={
                      key === 'soft'
                        ? 'Smoothed — radar detail is 1.1 km, so nothing real is lost'
                        : key === 'sharp'
                          ? 'Hard cell edges, as the data comes'
                          : 'A balance of the two'
                    }
                    onClick={() => updatePrefs({ sharpness: key as RadarSharpness })}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {productOptions.length > 1 && (
                <div className="radar-seg" role="group" aria-label="Radar product">
                  {productOptions.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      aria-pressed={(prefs.product ?? 'reflectivity') === p.id}
                      title={
                        p.categorical
                          ? 'NOAA precipitation type — separates frozen precipitation from rain'
                          : 'Radar intensity — how hard it is coming down'
                      }
                      onClick={() => updatePrefs({ product: p.id })}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
              {providerOptions.length > 1 && (
                <select
                  className="radar-provider"
                  aria-label="Radar source"
                  value={prefs.provider ?? providerOptions[0]?.id ?? ''}
                  onChange={(e) => updatePrefs({ provider: e.target.value })}
                >
                  {providerOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {categorical && !compact && (
            /*
             * NOAA publishes no colour key for this layer — GetLegendGraphic returns an
             * empty raster style. Say so rather than letting a reader assume which colour
             * means snow; in an ice event that assumption is the costly one.
             */
            <span style={{ flexBasis: '100%', fontSize: 11, color: '#7d8ea8', textAlign: 'right' }}>
              Precipitation type from NOAA — colours separate frozen precipitation from rain,
              but NOAA publishes no key.
            </span>
          )}
        </div>
      )}

      <div
        style={{
          position: 'absolute',
          right: compact ? 10 : 12,
          bottom: compact ? 10 : 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          zIndex: 2,
        }}
      >
        <button type="button" aria-label="Zoom in" className="map-btn hov" onClick={() => zoomBy(1)}>
          <Icon d={I.plus} size={18} />
        </button>
        <button type="button" aria-label="Zoom out" className="map-btn hov" onClick={() => zoomBy(-1)}>
          <Icon d={I.minus} size={18} />
        </button>
        <button type="button" aria-label="Recenter map" className="map-btn hov" onClick={recenter}>
          <Icon d={I.target} size={18} />
        </button>
      </div>
    </div>
  );
}
